import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FileDown, TrendingDown, FileText, BarChart3, List } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { MonthlySavingsChart } from "@/components/backtest/MonthlySavingsChart";
import { SupplierBreakdownTable } from "@/components/backtest/SupplierBreakdownTable";
import { InvoiceResultsTable } from "@/components/backtest/InvoiceResultsTable";
import { BacktestLayout } from "@/components/backtest/BacktestLayout";
import { useBacktestRun } from "@/hooks/useBacktestRuns";
import { formatCurrency } from "@/lib/currency";
import type { BacktestInvoiceRow } from "@/types/backtest";
import { useToast } from "@/hooks/use-toast";

function KpiCard({
  label,
  value,
  sub,
  highlight,
}: {
  label: string;
  value: string;
  sub?: string;
  highlight?: "good" | "bad" | "neutral";
}) {
  const colors = {
    bad: "text-destructive",
    good: "text-green-700",
    neutral: "text-foreground",
  };
  return (
    <Card>
      <CardContent className="pt-5 pb-5">
        <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{label}</p>
        <p className={`text-2xl font-bold ${colors[highlight ?? "neutral"]}`}>{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function exportToCSV(rows: BacktestInvoiceRow[], runName: string) {
  const headers = [
    "Row",
    "Supplier",
    "Invoice #",
    "Invoice Date",
    "Amount",
    "Payment Terms",
    "Payment Date",
    "Discount Deadline",
    "Was Eligible",
    "Discount Captured",
    "Missed Savings",
    "Captured Savings",
    "Parse Error",
  ];

  const csvRows = rows.map((r) => [
    r.row_number,
    r.supplier_name ?? "",
    r.invoice_number ?? "",
    r.invoice_date ?? "",
    r.invoice_amount ?? "",
    r.payment_terms ?? "",
    r.actual_payment_date ?? "",
    r.discount_deadline ?? "",
    r.was_eligible ? "Yes" : "No",
    r.discount_captured ? "Yes" : "No",
    r.missed_savings,
    r.captured_savings,
    r.parse_error ?? "",
  ]);

  const content = [headers, ...csvRows]
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
    .join("\n");

  const blob = new Blob([content], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${runName.replace(/\s+/g, "_")}_backtest_results.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function BacktestResultsPage() {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { run, invoiceRows, isLoading, getDownloadUrl } = useBacktestRun(runId);
  const [isDownloadingOriginal, setIsDownloadingOriginal] = useState(false);

  const handleDownloadOriginal = async () => {
    setIsDownloadingOriginal(true);
    const url = await getDownloadUrl();
    setIsDownloadingOriginal(false);
    if (!url) {
      toast({
        title: "File unavailable",
        description: "Could not retrieve the original file.",
        variant: "destructive",
      });
      return;
    }
    const a = document.createElement("a");
    a.href = url;
    a.download = run?.file_name ?? "original_file.csv";
    a.click();
  };

  if (isLoading) {
    return (
      <BacktestLayout backLabel="All Analyses" backTo="/backtest">
        <div className="max-w-5xl mx-auto space-y-4">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-4 gap-3">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
          <Skeleton className="h-64" />
        </div>
      </BacktestLayout>
    );
  }

  if (!run) {
    return (
      <BacktestLayout backLabel="All Analyses" backTo="/backtest">
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <p className="text-muted-foreground">Analysis not found.</p>
          <Button variant="outline" onClick={() => navigate("/backtest")}>
            Back to Analyses
          </Button>
        </div>
      </BacktestLayout>
    );
  }

  const dateRange =
    run.date_range_start && run.date_range_end
      ? `${run.date_range_start} – ${run.date_range_end}`
      : null;

  const totalOpportunity = run.total_missed_savings + run.total_captured_savings;

  return (
    <BacktestLayout backLabel="All Analyses" backTo="/backtest">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            {run.client_name && (
              <p className="text-sm text-muted-foreground mb-0.5">
                Early payment performance for{" "}
                <span className="font-medium text-foreground">{run.client_name}</span>
              </p>
            )}
            <h2 className="text-2xl font-display font-bold tracking-tight">{run.name}</h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              Analysed{" "}
              {new Date(run.created_at).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
              {dateRange && ` · Data from ${dateRange}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {run.file_name && (
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={handleDownloadOriginal}
                disabled={isDownloadingOriginal}
              >
                <FileText className="w-3.5 h-3.5 mr-1.5" />
                {isDownloadingOriginal ? "Preparing…" : "Download Original"}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              onClick={() => exportToCSV(invoiceRows, run.name)}
            >
              <FileDown className="w-3.5 h-3.5 mr-1.5" />
              Export Results
            </Button>
          </div>
        </div>

        {/* Hero */}
        <Card className="border-destructive/30 bg-destructive/5">
          <CardContent className="pt-6 pb-6">
            <div className="flex items-center gap-4 flex-wrap">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                  Total Missed Savings
                </p>
                <p className="text-4xl font-bold text-destructive">
                  {formatCurrency(run.total_missed_savings)}
                </p>
                {totalOpportunity > 0 && (
                  <p className="text-sm text-muted-foreground mt-1">
                    out of {formatCurrency(totalOpportunity)} in total discount opportunities
                  </p>
                )}
              </div>
              {run.total_captured_savings > 0 && (
                <div className="ml-auto text-right">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                    Savings Captured
                  </p>
                  <p className="text-2xl font-bold text-green-700">
                    {formatCurrency(run.total_captured_savings)}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* KPI row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <KpiCard
            label="Capture Rate"
            value={`${run.capture_rate.toFixed(1)}%`}
            sub={`of eligible discounts taken`}
            highlight={run.capture_rate >= 75 ? "good" : run.capture_rate >= 40 ? "neutral" : "bad"}
          />
          <KpiCard
            label="Invoices Analysed"
            value={run.total_invoices.toLocaleString()}
            sub={`${run.eligible_invoices.toLocaleString()} with discount terms`}
          />
          <KpiCard
            label="Discounts Missed"
            value={run.discounts_missed.toLocaleString()}
            sub={`invoices paid after deadline`}
            highlight={run.discounts_missed > 0 ? "bad" : "good"}
          />
          <KpiCard
            label="Discounts Captured"
            value={run.discounts_captured.toLocaleString()}
            sub={`invoices paid within window`}
            highlight={run.discounts_captured > 0 ? "good" : "neutral"}
          />
        </div>

        {/* Charts + Supplier table */}
        <div className="grid lg:grid-cols-2 gap-5">
          <Card>
            <CardHeader className="pb-2 pt-4 px-5">
              <CardTitle className="text-sm font-semibold flex items-center gap-1.5">
                <BarChart3 className="w-4 h-4 text-muted-foreground" />
                Missed vs. Captured by Month
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 pb-4">
              <MonthlySavingsChart rows={invoiceRows} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2 pt-4 px-5">
              <CardTitle className="text-sm font-semibold flex items-center gap-1.5">
                <TrendingDown className="w-4 h-4 text-muted-foreground" />
                Suppliers with Most Missed Savings
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <SupplierBreakdownTable rows={invoiceRows} />
            </CardContent>
          </Card>
        </div>

        {/* Invoice-level table */}
        <Card>
          <CardHeader className="pb-2 pt-4 px-5">
            <CardTitle className="text-sm font-semibold flex items-center gap-1.5">
              <List className="w-4 h-4 text-muted-foreground" />
              All Invoices
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <InvoiceResultsTable rows={invoiceRows} />
          </CardContent>
        </Card>
      </div>
    </BacktestLayout>
  );
}
